import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Modified learning progress data with increasing intervals between reviews (spaced repetition)
export const learningProgressData = [
  // Initial learning phase - steep initial drop (First exposure)
  { day: 'Day 1', accuracy: 72, practice: true, label: 'Initial learning: first exposure' },
  { day: 'Day 2', accuracy: 50, practice: false, label: 'Steep forgetting - new memory fragile' },
  { day: 'Day 3', accuracy: 40, practice: false, label: 'Rapid memory decay' },
  { day: 'Day 4', accuracy: 34, practice: false, label: 'Significant retention loss' },
  
  // First review after 4 days (short interval)
  { day: 'Day 5', accuracy: 80, practice: true, label: 'First review: reinforcement after 4 days' },

  // First spaced decay with natural variation
  { day: 'Day 6', accuracy: 74, practice: false, label: 'Decay after first review' },
  { day: 'Day 7', accuracy: 71, practice: false, label: 'Slight recovery then drop' }, // Was 69
  { day: 'Day 8', accuracy: 67, practice: false, label: 'Decline moderating' }, // Was 65
  { day: 'Day 9', accuracy: 63, practice: false, label: 'Gradual decline' }, // Was 62
  { day: 'Day 10', accuracy: 60, practice: false, label: 'Memory fading slower' }, // Was 59
  { day: 'Day 11', accuracy: 57, practice: false, label: 'Continued decay' }, // Was 56
  
  // Second review after 7 days (longer interval)
  { day: 'Day 12', accuracy: 88, practice: true, label: 'Second review: after 7 days (stronger memory)' },
  
  // Second decay with variation
  { day: 'Day 13', accuracy: 86, practice: false, label: 'Small decay after review' }, // Was 85
  { day: 'Day 14', accuracy: 83, practice: false, label: 'Decline continuing' }, // Was 82
  { day: 'Day 15', accuracy: 80, practice: false, label: 'Slight bounce back' }, // Was 79
  { day: 'Day 16', accuracy: 77, practice: false, label: 'Retention fading' }, // Was 76
  { day: 'Day 17', accuracy: 75, practice: false, label: 'Memory stabilizing' }, // Was 74
  { day: 'Day 18', accuracy: 73, practice: false, label: 'Continued slow decline' }, // Was 72
  
  // More days before next review with natural variation
  { day: 'Day 19', accuracy: 71, practice: false, label: 'Slow decline continues' }, // Was 70
  { day: 'Day 20', accuracy: 69, practice: false, label: 'Memory consolidating' }, // Was 68
  { day: 'Day 21', accuracy: 67, practice: false, label: 'Gradual forgetting' }, // Was 66
  { day: 'Day 22', accuracy: 65, practice: false, label: 'Slight uptick in retention' }, // Was 64

  // Third review after 11 days (even longer interval)
  { day: 'Day 23', accuracy: 92, practice: true, label: 'Third review: after 11 days (near mastery)' },
  
  // Third decay with variation
  { day: 'Day 24', accuracy: 91, practice: false, label: 'Minimal decay' }, // Was 90
  { day: 'Day 25', accuracy: 89, practice: false, label: 'Continued decline' }, // Was 88
  { day: 'Day 26', accuracy: 87, practice: false, label: 'Retention slowly fading' }, // Was 86
  { day: 'Day 27', accuracy: 85, practice: false, label: 'Memory consolidation with decay' }, // Was 84
  { day: 'Day 28', accuracy: 83, practice: false, label: 'Still declining' }, // Was 82
  
  // More extended period before next review with natural variation
  { day: 'Day 29', accuracy: 82, practice: false, label: 'Slow decline continues' }, // Was 81
  { day: 'Day 30', accuracy: 81, practice: false, label: 'Retention stable' }, // Was 80
  { day: 'Day 31', accuracy: 80, practice: false, label: 'Slight improvement' }, // Was 79
  { day: 'Day 32', accuracy: 79, practice: false, label: 'Memory durable' }, // Was 78
  { day: 'Day 33', accuracy: 78, practice: false, label: 'Slow decay' }, // Was 77
  { day: 'Day 34', accuracy: 77, practice: false, label: 'Retention holding' }, // Was 76
  { day: 'Day 35', accuracy: 76, practice: false, label: 'Gradual decline' }, // Was 75
  { day: 'Day 36', accuracy: 75, practice: false, label: 'Memory consolidated' }, // Was 74

  // Fourth review after 14 days (longest interval yet)
  { day: 'Day 37', accuracy: 94, practice: true, label: 'Fourth review: after 14 days (expert level)' },
  
  // Fourth decay with variation
  { day: 'Day 38', accuracy: 93, practice: false, label: 'Noticeable decay' }, // Was 92
  { day: 'Day 39', accuracy: 91, practice: false, label: 'Continued decline' }, // Was 90
  { day: 'Day 40', accuracy: 89, practice: false, label: 'Memory fading slowly' }, // Was 88
  { day: 'Day 41', accuracy: 87, practice: false, label: 'Slight recovery' }, // Was 86
  { day: 'Day 42', accuracy: 85, practice: false, label: 'Still declining' }, // Was 84

  // Extended period before fifth review (20-day interval) - falling to 77%
  { day: 'Day 43', accuracy: 84, practice: false, label: 'Slow decay continues' },
  { day: 'Day 44', accuracy: 83, practice: false, label: 'Gradual forgetting' },
  { day: 'Day 45', accuracy: 82, practice: false, label: 'Long-term retention with decay' },
  { day: 'Day 46', accuracy: 81, practice: false, label: 'Memory holding well' },
  { day: 'Day 47', accuracy: 80, practice: false, label: 'Memory stable' },
  { day: 'Day 48', accuracy: 79, practice: false, label: 'Persistent slow decline' },
  { day: 'Day 49', accuracy: 79, practice: false, label: 'Plateau in retention' },
  { day: 'Day 50', accuracy: 78, practice: false, label: 'Mastery slowly fading' },
  { day: 'Day 51', accuracy: 78, practice: false, label: 'Stable retention' },
  { day: 'Day 52', accuracy: 77, practice: false, label: 'Continued slow decline' },
  { day: 'Day 53', accuracy: 77, practice: false, label: 'Memory holding steady' },
  { day: 'Day 54', accuracy: 77, practice: false, label: 'Stabilized at higher level' },
  { day: 'Day 55', accuracy: 77, practice: false, label: 'Approaching review threshold' },
  { day: 'Day 56', accuracy: 77, practice: false, label: 'Memory needs reinforcement' },
  
  // Fifth review after 20 days (extended interval for mastery maintenance)
  { day: 'Day 57', accuracy: 96, practice: true, label: 'Fifth review: after 20 days (mastery maintenance)' },
  
  // Fifth decay with natural variation
  { day: 'Day 58', accuracy: 95, practice: false, label: 'Minimal decay after review' },
  { day: 'Day 59', accuracy: 94, practice: false, label: 'Slow decline begins' },
  { day: 'Day 60', accuracy: 93, practice: false, label: 'Continued gradual decline' },
  { day: 'Day 61', accuracy: 92, practice: false, label: 'High retention maintained' },
  { day: 'Day 62', accuracy: 91, practice: false, label: 'Memory stable at high level' },
  { day: 'Day 63', accuracy: 90, practice: false, label: 'Slow decay continues' },
  { day: 'Day 64', accuracy: 89, practice: false, label: 'Slight bounce' },
  { day: 'Day 65', accuracy: 88, practice: false, label: 'Gradual forgetting' },
  { day: 'Day 66', accuracy: 87, practice: false, label: 'Long-term retention holding' },
  { day: 'Day 67', accuracy: 86, practice: false, label: 'Slow decline continues' },
  { day: 'Day 68', accuracy: 85, practice: false, label: 'Gradual forgetting at mastery level' },
  { day: 'Day 69', accuracy: 84, practice: false, label: 'Retention remains strong' },
  { day: 'Day 70', accuracy: 83, practice: false, label: 'End of 70-day period - strong retention maintained' },
];

// Custom dot component: size bigger for review, color by accuracy
const CustomDot = ({ cx, cy, payload }) => {
    const size = payload.practice ? 8 : 5; // bigger for review
    let fillColor;

    if (payload.accuracy >= 80) fillColor = '#10b981'; // green
    else if (payload.accuracy >= 40) fillColor = '#eab308'; // yellow (changed from orange)
    else fillColor = '#ef4444'; // red

    return (
        <circle
            cx={cx}
            cy={cy}
            r={size}
            fill={fillColor}
            stroke="#fff"
            strokeWidth={2}
        />
    );
};

// Custom tooltip
const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        const statusColor = data.accuracy >= 80 ? 'text-green-600 bg-green-50' : 
                           data.accuracy >= 40 ? 'text-yellow-600 bg-yellow-50' : 'text-red-600 bg-red-50';
        const statusText = data.accuracy >= 80  ? 'Very Good' : 
                          data.accuracy >= 40  ? 'Needs practice' : 'Poor';

        return (
            <div className="bg-white p-3 border border-gray-200 rounded-lg shadow">
                <p className="font-semibold text-gray-800">{data.day}</p>
                <div className="flex items-center justify-between mt-1">
                    <span className="text-sm text-gray-700">Accuracy:</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${statusColor}`}>
                        {data.accuracy}% ({statusText})
                    </span>
                </div>
                {data.label && <p className="text-xs text-blue-600 mt-2">{data.label}</p>}
                {data.practice && <p className="text-xs text-green-600 mt-1">✓ Practice session</p>}
            </div>
        );
    }
    return null;
};

const LearningProgressChart = () => {
  return (
    <div className="bg-white rounded-xl p-6 border border-gray-200">
      <h4 className="text-xl font-bold text-gray-800 mb-6">
        Learning Progress Over Time with Spaced Repetition
      </h4>

      {/* IMPROVED EXPLANATION SECTION */}
      <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
        <div className="flex items-start mb-3">
          <div className="text-lg mr-3">🔍</div>
          <div>
            <h5 className="font-bold text-gray-800 mb-2 text-base">
              How to Read This Chart
            </h5>
            <div className="space-y-2">
              <div className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center mr-2">
                  <div className="w-3 h-3 bg-indigo-600 rounded-full"></div>
                </div>
                <p className="text-base text-gray-700">
                  <strong>Larger dots</strong> mark review/practice sessions
                </p>
              </div>
              <div className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center mr-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                </div>
                <p className="text-base text-gray-700">
                  <span className="font-semibold">Green dots:</span> High accuracy (80-100%)
                </p>
              </div>
              <div className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center mr-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                </div>
                <p className="text-base text-gray-700">
                  <span className="font-semibold">Yellow dots:</span> Needs practice (40-79%)
                </p>
              </div>
              <div className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center mr-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                </div>
                <p className="text-base text-gray-700">
                  <span className="font-semibold">Red dots:</span> Low accuracy (0-39%)
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-white rounded border border-blue-100">
          <h6 className="font-bold text-indigo-700 mb-2 text-sm">🎯 Key Observation</h6>
          <p className="text-base text-gray-800">
            <strong>Review intervals increase</strong> following spaced repetition principles: 
            <span className="font-semibold text-indigo-600"> 4 days → 7 days → 11 days → 14 days → 20 days</span>. 
            Each review reinforces memory while allowing progressively longer intervals.
          </p>
        </div>
      </div>

      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={learningProgressData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="day" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
            <YAxis 
              domain={[0, 100]} 
              tick={{ fontSize: 12 }} 
              label={{ 
                value: 'Accuracy (%)', 
                angle: -90, 
                position: 'insideLeft',
                offset: -10,
                style: { fontSize: 14, fontWeight: 'bold' }
              }} 
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="accuracy"
              stroke="#6366f1"
              strokeWidth={3}
              dot={<CustomDot />}
              activeDot={{ r: 8 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-4 mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center">
          <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
          <span className="text-base font-medium">80-100%: Good</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-yellow-500 rounded-full mr-2"></div>
          <span className="text-base font-medium">40-79%: Needs practice</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-red-500 rounded-full mr-2"></div>
          <span className="text-base font-medium">0-39%: Poor</span>
        </div>
        <div className="flex items-center">
          <div className="w-5 h-5 border-2 border-indigo-600 rounded-full mr-2 flex items-center justify-center">
            <div className="w-3 h-3 bg-indigo-600 rounded-full"></div>
          </div>
          <span className="text-base font-medium">Review session</span>
        </div>
      </div>

      {/* IMPROVED EXPLANATION SECTION WITH BETTER VISUAL HIERARCHY */}
      <div className="bg-white rounded-lg p-6 border border-gray-200 mt-8">
        <h4 className="font-bold text-gray-800 mb-6 text-xl border-b pb-3 flex items-center">
          <span className="mr-2">🔑</span>
          Key Pattern: Spaced Repetition with Increasing Intervals
        </h4>

        <div className="space-y-6">
          <div className="bg-blue-50 p-5 rounded-xl border-2 border-blue-200">
            <h5 className="font-bold text-blue-800 mb-3 text-lg">Core Principle</h5>
            <p className="text-base text-gray-800 leading-relaxed">
              <strong>Spaced repetition works by increasing intervals between reviews just as memory 
              is about to fade.</strong> This optimizes learning efficiency - reinforcing memories 
              at the optimal moment before they're forgotten, requiring fewer total reviews 
              for long-term retention.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-green-50 p-5 rounded-xl border-2 border-green-200">
              <h5 className="font-bold text-green-800 mb-4 text-lg flex items-center">
                <span className="mr-2">📈</span>
                Pattern Highlights
              </h5>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="bg-white rounded-lg px-3 py-1 mr-3 border border-green-200">
                    <span className="font-bold text-green-700">4d</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800 block">First interval:</span>
                    <span className="text-gray-700 text-sm">Quick review while memory is fresh</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-white rounded-lg px-3 py-1 mr-3 border border-green-200">
                    <span className="font-bold text-green-700">7d</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800 block">Second interval:</span>
                    <span className="text-gray-700 text-sm">Slightly longer wait as memory stabilizes</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-white rounded-lg px-3 py-1 mr-3 border border-green-200">
                    <span className="font-bold text-green-700">11d</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800 block">Third interval:</span>
                    <span className="text-gray-700 text-sm">Memory durable enough for longer intervals</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-white rounded-lg px-3 py-1 mr-3 border border-green-200">
                    <span className="font-bold text-green-700">14d</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800 block">Fourth interval:</span>
                    <span className="text-gray-700 text-sm">Extended interval for strong retention</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-white rounded-lg px-3 py-1 mr-3 border border-green-200">
                    <span className="font-bold text-green-700">20d</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800 block">Fifth interval:</span>
                    <span className="text-gray-700 text-sm">Extended maintenance interval (37 to 57)</span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-purple-50 p-5 rounded-xl border-2 border-purple-200">
              <h5 className="font-bold text-purple-800 mb-4 text-lg flex items-center">
                <span className="mr-2">🎯</span>
                Key Results
              </h5>
              <ul className="space-y-3">
                <li className="flex justify-between items-center py-2 border-b border-purple-100">
                  <span className="text-gray-700">Starting accuracy:</span>
                  <span className="font-bold text-gray-800 text-lg">72%</span>
                </li>
                <li className="flex justify-between items-center py-2 border-b border-purple-100">
                  <span className="text-gray-700">Peak accuracy:</span>
                  <span className="font-bold text-green-600 text-lg">96%</span>
                </li>
                <li className="flex justify-between items-center py-2 border-b border-purple-100">
                  <span className="text-gray-700">Accuracy at Day 70:</span>
                  <span className="font-bold text-green-600 text-lg">83%</span>
                </li>
                <li className="flex justify-between items-center py-2 border-b border-purple-100">
                  <span className="text-gray-700">Accuracy improvement:</span>
                  <span className="font-bold text-green-600 text-lg">+11% (net gain)</span>
                </li>
                <li className="flex justify-between items-center py-2">
                  <span className="text-gray-700">Time span:</span>
                  <span className="font-bold text-gray-800 text-lg">70 days</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-yellow-50 p-5 rounded-xl border-2 border-yellow-200">
            <h5 className="font-bold text-yellow-800 mb-3 text-lg flex items-center">
              <span className="mr-2">💡</span>
              Important Insight
            </h5>
            <p className="text-base text-gray-800 leading-relaxed">
              The <strong className="text-yellow-700">20-day interval between Day 37 and Day 57</strong> demonstrates that 
              well-established memories remain stable for extended periods. Accuracy only drops 
              from 94% to 77% over 20 days, showing that 
              <strong> spaced repetition creates highly durable long-term memories</strong> that 
              can be maintained with infrequent reviews while staying above 75% accuracy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningProgressChart;